import { useSnackbar } from 'notistack';
import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import AuthService from '@services/AuthService';
import BeneficiaryService from '@services/BeneficiaryService';
import CompanyService from '@services/CompanyService';
import FinancialService from '@services/FinancialService';

import { toastifyApiErrors } from '@utils';
import { CompanySetupQueryKeysEnum } from 'constants/private/companySetup/companySetupQueryKeys';

export function useCreateSession() {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  const authService = new AuthService();
  const companyService = new CompanyService();
  const financialService = new FinancialService();
  const beneficiaryService = new BeneficiaryService();

  const handleCreateSession = useCallback(
    async (partnerCompany: string, companyId: string) => {
      try {
        await authService.createSession({ partnerCompany, companyId });
        return true;
      } catch (error) {
        toastifyApiErrors(error, enqueueSnackbar);
        return false;
      }
    },
    [enqueueSnackbar],
  );

  const handleRemoveFromNewCompanies = useCallback(
    async (companyId: string) => {
      try {
        await companyService.removeIsNewCompanyStatus({ id: companyId });

        const usersQueryKey = [
          CompanySetupQueryKeysEnum.COMPANY_SETUP_LIST_STATE,
        ];
        queryClient.removeQueries({ queryKey: usersQueryKey, exact: true });
        await queryClient.refetchQueries({
          queryKey: usersQueryKey,
        });
      } catch (error) {
        toastifyApiErrors(error, enqueueSnackbar);
      }
    },
    [],
  );

  const handleMonthlyFeePreviewReport = useCallback(async () => {
    try {
      return await financialService.getMonthlyFeePreviewReport();
    } catch (error) {
      toastifyApiErrors(error, enqueueSnackbar);
      return undefined;
    }
  }, []);

  const handleCoparticipationPreviewReport = useCallback(async () => {
    try {
      return await financialService.getCoparticipationPreviewReport();
    } catch (error) {
      toastifyApiErrors(error, enqueueSnackbar);
      return undefined;
    }
  }, []);

  const handleSuspendedBeneficiary = useCallback(async () => {
    try {
      return await beneficiaryService.getSuspendedBeneficiary();
    } catch (error) {
      toastifyApiErrors(error, enqueueSnackbar);
      return undefined;
    }
  }, []);

  const handleBeneficiaryServicesExists = useCallback(
    async (partnerCompany: string) => {
      try {
        return await beneficiaryService.getBeneficiaryServicesExists(
          partnerCompany,
        );
      } catch (error) {
        toastifyApiErrors(error, enqueueSnackbar);
        return undefined;
      }
    },
    [],
  );

  const handleAccessResponsibilityLetter = useCallback(
    async (partnerCompany: string) => {
      try {
        return await companyService.accessResponsibilityLetter({
          partnerCompany,
        });
      } catch (error) {
        toastifyApiErrors(error, enqueueSnackbar);
        return undefined;
      }
    },
    [],
  );

  const handleCurrentCompany = useCallback(async () => {
    try {
      return await companyService.getCurrentCompany();
    } catch (error) {
      toastifyApiErrors(error, enqueueSnackbar);
      return undefined;
    }
  }, []);

  return {
    handleCreateSession,
    handleRemoveFromNewCompanies,
    handleMonthlyFeePreviewReport,
    handleCoparticipationPreviewReport,
    handleSuspendedBeneficiary,
    handleBeneficiaryServicesExists,
    handleAccessResponsibilityLetter,
    handleCurrentCompany,
  };
}
